import { safeTextEditDiagnostic } from "@shared/text-edit-diagnostics";
import { useCallback, useEffect, useRef, useState } from "react";
import { readImageEditStatus, waitForImageEditCheck } from "@/lib/image-edit-observation";
import {
  readPendingImageEdits, updatePendingImageEdit, clearPendingImageEdit,
  hasImageEditModalOwner, persistPendingImageEdit, imageEditPhaseLabel,
  subscribePendingImageEdits,
  imageEditOwnerKey,
  readImageEditSubmissions,
  type PendingImageEdit,
} from "@/lib/pending-image-edits";
import { usePendingImageEditsRevision } from "./use-pending-image-edits";
import { apiRequest } from "@/lib/queryClient";

export interface MyAdsEditState {
  jobId: string;
  label: string;
  busy: boolean;
  error?: string;
  canRetrySave: boolean;
}

// Owns only new, explicitly targeted My Ads edits. No URL-based historical
// replay, and no dependency on the current filtered/paginated library listing.
export function useMyAdsImageEdits(brandId: string, onSaved: () => void) {
  usePendingImageEditsRevision();
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const [applied, setApplied] = useState<Record<string, string>>({});
  const markApplied = useCallback((adId: string) => {
    setApplied(prev => ({ ...prev, [`${brandId}:${adId}`]: "Edit complete" }));
  }, [brandId]);

  useEffect(() => {
    let cancelled = false;
    const running = new Set<string>();
    const valid = (jobId: string) => {
      if (cancelled) return undefined;
      const edit = readPendingImageEdits().find(e => e.jobId === jobId);
      if (!edit || edit.target?.brandId !== brandId || edit.delivery === "review" || edit.error) return undefined;
      if (hasImageEditModalOwner(imageEditOwnerKey(edit.rootImageUrl ?? edit.sourceImageUrl, edit.target))) return undefined;
      return edit;
    };
    async function run(initial: PendingImageEdit) {
      const { jobId } = initial;
      if (running.has(jobId)) return;
      running.add(jobId);
      try {
        while (true) {
          let edit = valid(jobId);
          if (!edit) return;
          if (!edit.resultImageUrl) {
            try {
              const response = await readImageEditStatus(jobId);
              if (!valid(jobId)) return;
              if (response.ok) {
                const status = await response.json();
                if (!valid(jobId)) return;
                if (status.status === "failed") {
                  updatePendingImageEdit(jobId, { error: status.message || "Image edit failed.",
                    diagnostic: safeTextEditDiagnostic(status.diagnostic) });
                  return;
                }
                if (status.status === "ready" && status.image_url) {
                  updatePendingImageEdit(jobId, { delivery: "background", resultImageUrl: status.image_url, phase: "saving" });
                } else if (edit.phase !== status.phase) {
                  updatePendingImageEdit(jobId, { phase: status.phase });
                }
              }
            } catch {
              if (valid(jobId)?.phase !== "checking" && valid(jobId)) {
                updatePendingImageEdit(jobId, { phase: "checking" });
              }
            }
            edit = valid(jobId);
            if (!edit) return;
          }
          if (edit.resultImageUrl && edit.target) {
            const { adId } = edit.target;
            const url = edit.resultImageUrl;
            const refresh = onSavedRef.current;
            try {
              await persistPendingImageEdit(jobId, async () => {
                await apiRequest("POST", `/api/generated-ads/${adId}/edit-in-place`, { imageUrl: url });
              });
            } catch (error) {
              // Retain this exact output; retrying persistence never renders.
              updatePendingImageEdit(jobId, {
                error: `Couldn't save edit: ${error instanceof Error ? error.message : "Please retry saving."}`,
              });
              return;
            }
            // Refresh even if navigation happened during the save. Only state
            // belonging to this still-mounted owner is updated.
            refresh();
            if (!cancelled) markApplied(adId);
            return;
          }
          await waitForImageEditCheck(jobId);
        }
      } finally {
        running.delete(jobId);
      }
    }
    const scan = () => {
      if (cancelled) return;
      for (const edit of readPendingImageEdits()) {
        if (edit.target?.surface === "my-ads" && edit.target.brandId === brandId && valid(edit.jobId)) void run(edit);
      }
    };
    const unsubscribe = subscribePendingImageEdits(scan);
    scan();
    // A tracker update must not cancel an active loop. Only brand/navigation
    // changes cancel ownership; notifications are handled by a separate scan.
    return () => { cancelled = true; unsubscribe(); };
  }, [brandId, markApplied]);

  const states: Record<string, MyAdsEditState> = {};
  // Show progress before the submit response/job ID exists as well. Closing
  // the modal must not leave an apparently idle tile during admission.
  for (const target of readImageEditSubmissions()) {
    if (target.brandId === brandId) states[target.adId] = {
      jobId: "", label: "Starting edit…", busy: true, canRetrySave: false,
    };
  }
  for (const edit of readPendingImageEdits()) {
    if (edit.target?.brandId !== brandId) continue;
    states[edit.target.adId] = {
      jobId: edit.jobId,
      label: edit.error
        ? (edit.resultImageUrl ? `Edit complete — awaiting save retry. ${edit.error}` : edit.error)
        : (edit.delivery === "review" ? "Edit ready for review" : imageEditPhaseLabel(edit.phase)),
      busy: !edit.error && edit.delivery !== "review",
      error: edit.error,
      canRetrySave: !!edit.error && !!edit.resultImageUrl,
    };
  }
  return {
    states,
    applied: Object.fromEntries(Object.entries(applied)
      .filter(([key]) => key.startsWith(`${brandId}:`))
      .map(([key, label]) => [key.slice(brandId.length + 1), label])),
    markApplied,
    retrySave: (jobId: string) => updatePendingImageEdit(jobId, { error: undefined, delivery: "background", phase: "saving" }),
    discard: clearPendingImageEdit,
  };
}
